import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getPaymentStatus } from '../services/api';
import LoadingSpinner from './LoadingSpinner';

function PaymentSuccess() {
  const { paymentId, merchantSlug } = useParams();
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPaymentStatus();
  }, [paymentId]);

  const fetchPaymentStatus = async () => {
    try {
      const response = await getPaymentStatus(paymentId);
      setPayment(response.data);
    } catch (error) {
      console.error('Error fetching payment status:', error);
      // 如果無法取得付款狀態，重定向到錯誤頁面
      navigate(`/payment/error/${paymentId}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <span className="material-symbols-outlined text-6xl text-gray-400">error</span>
        <p className="text-text-primary text-lg">找不到付款資訊</p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto p-6">
      {/* Success Icon */}
      <div className="flex flex-col items-center gap-4 p-6 bg-white dark:bg-background-dark rounded-xl shadow-sm mb-6">
        <div className="flex items-center justify-center w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30">
          <span className="material-symbols-outlined text-green-500 text-5xl">check_circle</span>
        </div>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-text-light dark:text-text-dark mb-2">
            付款成功！
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            您的體驗已確認，感謝您的付款。
          </p>
        </div>
      </div>

      {/* Payment Details */}
      <div className="bg-white dark:bg-background-dark rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-lg font-semibold text-text-light dark:text-text-dark mb-4">付款詳情</h2>
        
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">付款金額</span>
            <span className="text-text-light dark:text-text-dark font-medium">NT$ {payment.amount}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">付款狀態</span>
            <span className="text-green-600 dark:text-green-400 font-medium">已完成</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">交易編號</span>
            <span className="text-text-light dark:text-text-dark font-mono text-sm">{payment.transaction_id}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">付款時間</span>
            <span className="text-text-light dark:text-text-dark">
              {new Date(payment.paid_at).toLocaleString('zh-TW')}
            </span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        <Link
          to={`/confirmation/${payment.booking_id}`}
          className="w-full h-12 flex items-center justify-center bg-primary text-white text-base font-bold rounded-lg hover:bg-primary/90 transition-colors"
        >
          查看體驗詳情
        </Link>
        
        <Link
          to="/bookings"
          className="w-full h-12 flex items-center justify-center bg-gray-200 dark:bg-gray-700 text-text-light dark:text-text-dark text-base font-bold rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
        >
          查看所有體驗
        </Link>
        
        <Link
          to={`/${merchantSlug}`}
          className="w-full h-12 flex items-center justify-center text-primary text-base font-bold rounded-lg hover:bg-primary/10 transition-colors"
        >
          返回首頁
        </Link>
      </div>

      {/* Receipt Notice */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <div className="flex items-start gap-2">
          <span className="material-symbols-outlined text-blue-500 mt-0.5">receipt</span>
          <div>
            <p className="text-sm font-medium text-blue-700 dark:text-blue-400">付款收據</p>
            <p className="text-xs text-blue-600 dark:text-blue-500 mt-1">
              付款收據已發送至您的電子郵件，請妥善保存作為付款憑證。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentSuccess;